// «Пульс класса» — экран прохождения по ссылке класса.
//
// Ребёнок приходит по ссылке /pulse/<entry_token> без учётной записи. Экран:
//   вход (student_intro, проверка «я не робот») → вопросы по одному, любой
//   пропускается → одна отправка → финал (student_finish) или состояние.
//
// ПРАВИЛА (от методолога, не менять без него):
//   • язык — из класса в ссылке (pulse_wave_classes.locale: az, ru или en),
//     не из браузера: в одном классе все видят одно и то же;
//   • пока сервер класс не назвал, состояния из locale_unknown (неверная
//     ссылка, нет сети на входе) показываются на всех языках из
//     locale_unknown.show сразу, сверху вниз в его порядке — угадывать язык
//     нельзя;
//   • кнопки «Прослушать вопрос» нет, пока нет настоящего аудио — текст
//     ui.listen в данных есть, в интерфейсе его быть не должно;
//   • тексты — только из src/data; исход показывается ровно тем состоянием,
//     которое называет pulseStateFor, похожий текст вместо отсутствующего не
//     подставляется;
//   • send_failed не очищает ответы: они остаются в памяти страницы, и
//     «Отправить ещё раз» уходит с теми же ответами.
//
// Ответы живут только в памяти вкладки — ни в localStorage, ни в
// sessionStorage: школьные компьютеры общие. В sessionStorage лежит лишь
// билет вкладки, чтобы перезагрузка не заставляла проходить капчу заново.
// Страница не отдаёт адрес с токеном в Referer (no-referrer).

import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { TurnstileWidget } from "@/components/security/TurnstileWidget";
import screens from "@/data/pulse_screens_v0_2.json";
import { usePublishPageLanguage } from "@/lib/page-language";
import { getPulseEntry, startPulse, submitPulse, type PulseLocale } from "@/lib/pulse.functions";
import { pulseBank } from "@/pulse/banks";
import { pulseStateFor, type PulseScreenOutcome, type PulseStateKey } from "@/pulse/outcomes";
import { pulseNextStep, pulseSkipStep, type PulseAnswers } from "@/pulse/flow";
import { isPulseQuestionShown, prunePulseAnswers } from "@/pulse/validate";

const PALETTE = {
  bg: "#F0EDE5",
  paper: "#FBFAF7",
  ink: "#0F2A38",
  teal: "#1E5A6B",
  tealInk: "#143F4B",
  amber: "#F0A63A",
  line: "#E3E7EB",
  muted: "#6B8090",
};

type Lang = Record<PulseLocale, string>;
type Option = { id: string; label: Lang; emoji?: string; exclusive?: boolean };
type Question = { id: string; type: "scale" | "single" | "multi"; scale?: string; text: Lang; options?: Option[] };
type Scale = { options?: Option[]; values?: Option[] };
type StateTexts = { title: Lang; body: Lang; retry?: Lang };
type Answer = string | string[];

const STATES = screens.states as unknown as Record<string, StateTexts>;
const UI = screens.ui as unknown as Record<string, Lang>;

// Пока класс не известен — какие состояния и на каких языках. Список языков
// берётся из данных как есть, в их порядке. Что в нём только языки класса,
// держит verify:pulse-routes.
const LOCALE_UNKNOWN = screens.locale_unknown as unknown as { show: PulseLocale[]; states: string[] };
const UNKNOWN_LANGS = LOCALE_UNKNOWN.show;

// Сколько неудач входа подряд терпеть молча: короткий обрыв ребёнку объяснять не надо.
const ENTRY_QUIET_FAILURES = 1;
const ENTRY_RETRY_MS = 5000;

const ticketKey = (token: string) => `pulse-ticket:${token}`;
const readTicket = (token: string): string | null => {
  try {
    return sessionStorage.getItem(ticketKey(token));
  } catch {
    return null;
  }
};
const writeTicket = (token: string, ticket: string | null) => {
  try {
    if (ticket) sessionStorage.setItem(ticketKey(token), ticket);
    else sessionStorage.removeItem(ticketKey(token));
  } catch {
    // Хранилище закрыто (приватный режим) — билет останется только в памяти.
  }
};

export const Route = createFileRoute("/pulse/$token")({
  // Сервер печатает экран загрузки на азербайджанском (locale стартует с
  // "az"), значит и <html lang> — "az". Язык класса страница объявит сама,
  // когда узнает.
  loader: () => ({ pageLang: "az" as const }),
  head: () => ({
    meta: [
      { title: "Yolutap" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: PulsePage,
});

type Phase =
  | { kind: "loading" }
  | { kind: "intro" }
  | { kind: "questions" }
  | { kind: "sending" }
  | { kind: "finish" }
  | { kind: "state"; key: Exclude<PulseStateKey, "student_finish"> };

function PulsePage() {
  const { token } = Route.useParams();
  const [locale, setLocale] = useState<PulseLocale>("az");
  const [localeKnown, setLocaleKnown] = useState(false);
  const [bankVersion, setBankVersion] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [ticket, setTicket] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startOffline, setStartOffline] = useState(false);
  const [entryAttempt, setEntryAttempt] = useState(0);
  const entryFailures = useRef(0);

  usePublishPageLanguage(locale);
  const t = useCallback((x: Lang) => x[locale], [locale]);

  const show = useCallback((outcome: PulseScreenOutcome) => {
    const key = pulseStateFor(outcome);
    setPhase(key === "student_finish" ? { kind: "finish" } : { kind: "state", key });
  }, []);

  // Вход. Первая неудача — молча, индикатор; со второй подряд — entry_offline.
  // Автоповтор продолжается и на этом экране; кнопка повторяет сразу
  // (entryAttempt), удача убирает экран сама.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const res = await getPulseEntry({ data: { token } });
        if (cancelled) return;
        entryFailures.current = 0;
        if (res.locale) {
          setLocale(res.locale);
          setLocaleKnown(true);
        }
        if (res.outcome !== "ready") {
          show(res.outcome);
          return;
        }
        setBankVersion(res.bankVersion);
        const saved = readTicket(token);
        if (saved) {
          setTicket(saved);
          setPhase({ kind: "questions" });
        } else setPhase({ kind: "intro" });
      } catch {
        if (cancelled) return;
        entryFailures.current += 1;
        if (entryFailures.current > ENTRY_QUIET_FAILURES) show("entry_offline");
        timer = setTimeout(load, ENTRY_RETRY_MS);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, show, entryAttempt]);

  const bank = useMemo(() => (bankVersion ? pulseBank(bankVersion) : undefined), [bankVersion]);
  const questions = useMemo(() => (bank ? (bank.questions as unknown as Question[]) : []), [bank]);
  const visible = useMemo(
    () => (bank ? questions.filter((q) => isPulseQuestionShown(bank, q.id, answers)) : []),
    [bank, questions, answers],
  );
  const index = Math.max(0, visible.findIndex((q) => q.id === currentId));
  const current = visible[index];

  useEffect(() => {
    // Банка с этим именем в коде нет — вопросы сменились под классом.
    if (bankVersion && !bank) show("bank_changed");
  }, [bankVersion, bank, show]);

  // «Начать» ждёт токена от ЭТОГО показа капчи. Каждый вход на экран «Начать»
  // ставит виджет заново, а токен прошлого показа уже потрачен на прошлое
  // «Начать» — он не должен оживлять кнопку, пока новый виджет ещё крутится.
  // Колбэк Turnstile приходит асинхронно, после этой очистки.
  useEffect(() => {
    if (phase.kind === "intro") setCaptcha(null);
  }, [phase.kind]);

  const start = async () => {
    if (!captcha || starting) return;
    setStarting(true);
    try {
      const res = await startPulse({ data: { token, turnstileToken: captcha } });
      setStartOffline(false);
      if (res.outcome === "ok") {
        setTicket(res.ticket);
        writeTicket(token, res.ticket);
        // Токен капчи потрачен этим «Начать».
        setCaptcha(null);
        setPhase({ kind: "questions" });
      } else show(res.outcome);
    } catch {
      // Сбой сети на «Начать» — start_offline над той же кнопкой: второй кнопки
      // с тем же действием на экране не нужно. Токен Turnstile одноразовый,
      // поэтому виджет перерисовывается, и кнопка оживает с новым токеном.
      setStartOffline(true);
      setCaptcha(null);
      setCaptchaKey((k) => k + 1);
    } finally {
      setStarting(false);
    }
  };

  // Отправляются ровно те ответы, что передал шаг (pulseNextStep /
  // pulseSkipStep), а не состояние из замыкания: сразу после «Пропустить» оно
  // ещё старое, и снятый ответ ушёл бы вместе с анкетой.
  const submit = async (toSend: PulseAnswers) => {
    if (!bank) return;
    const t0 = ticket ?? readTicket(token);
    if (!t0) {
      // Билета нет (хранилище вкладки очищено) — нужна новая проверка на входе;
      // ответы остаются в памяти и уйдут после неё.
      setPhase({ kind: "intro" });
      return;
    }
    setPhase({ kind: "sending" });
    try {
      const res = await submitPulse({ data: { ticket: t0, answers: prunePulseAnswers(bank, toSend) } });
      // Любой ответ сервера окончателен для этого билета: повторять им нечего.
      setTicket(null);
      writeTicket(token, null);
      if (res.outcome === "ok") setAnswers({});
      show(res.outcome);
    } catch {
      // Сеть, 5xx, таймаут: ответы не трогаем, билет не гасим.
      show("send_failed");
    }
  };

  const choose = (q: Question, option: Option) => {
    setAnswers((prev) => {
      if (q.type !== "multi") return { ...prev, [q.id]: option.id };
      const was = Array.isArray(prev[q.id]) ? (prev[q.id] as string[]) : [];
      const exclusive = new Set((q.options ?? []).filter((o) => o.exclusive).map((o) => o.id));
      let next: string[];
      if (was.includes(option.id)) next = was.filter((x) => x !== option.id);
      else if (option.exclusive) next = [option.id];
      else next = [...was.filter((x) => !exclusive.has(x)), option.id];
      const copy = { ...prev };
      if (next.length) copy[q.id] = next;
      else delete copy[q.id];
      return copy;
    });
  };

  const go = (delta: 1 | -1) => {
    if (!bank || !current) return;
    if (delta === -1) {
      const target = visible[index - 1];
      if (target) setCurrentId(target.id);
      return;
    }
    const step = pulseNextStep(bank, answers, current.id);
    if ("next" in step) setCurrentId(step.next);
    else void submit(step.send);
  };

  const skip = () => {
    if (!bank || !current) return;
    // Пропуск снимает ответ на этот вопрос — на последнем так же, как на любом
    // другом: в отправку уходят ответы уже после снятия (pulseSkipStep).
    // Следующий вопрос считается по ним же: снятый ответ мог скрыть вопросы,
    // которые шли следом (c1, c2 при снятой травле).
    const { answers: nextAnswers, step } = pulseSkipStep(bank, answers, current.id);
    setAnswers(nextAnswers);
    if ("next" in step) setCurrentId(step.next);
    else void submit(step.send);
  };

  const optionsOf = (q: Question): Option[] => {
    if (q.type !== "scale") return q.options ?? [];
    const s = (bank?.scales as unknown as Record<string, Scale> | undefined)?.[q.scale ?? ""];
    return s?.options ?? s?.values ?? [];
  };

  const startOfflineTexts = STATES[pulseStateFor("start_offline")];

  return (
    <main style={{ background: PALETTE.bg, minHeight: "100dvh", color: PALETTE.ink }}>
      <div className="mx-auto flex w-full max-w-md flex-col gap-5 px-4 py-6">
        {phase.kind === "loading" && <Spinner />}

        {phase.kind === "intro" && (
          <Card>
            <h1 className="text-xl font-semibold leading-snug" style={{ color: PALETTE.tealInk }}>
              {t(screens.student_intro.title)}
            </h1>
            <p className="leading-relaxed">{t(screens.student_intro.body)}</p>
            <p className="text-sm" style={{ color: PALETTE.muted }}>
              {t(UI.progress_note)}
            </p>
            {/* Тексты экранов пульса утверждены отдельно (pulse_screens), и
                контакты поддержки в анкету ученика не добавляем: сбой капчи
                отсюда уходит только событием в Sentry. */}
            <TurnstileWidget
              key={captchaKey}
              fallback={false}
              onToken={setCaptcha}
              onExpired={() => setCaptcha(null)}
              onError={() => setCaptcha(null)}
            />
            {startOffline && (
              <div
                role="alert"
                className="flex flex-col gap-1 rounded-xl p-3"
                style={{ border: `1px solid ${PALETTE.amber}` }}
              >
                <p className="font-semibold" style={{ color: PALETTE.tealInk }}>
                  {t(startOfflineTexts.title)}
                </p>
                <p className="text-sm leading-relaxed">{t(startOfflineTexts.body)}</p>
              </div>
            )}
            <Primary onClick={start} disabled={!captcha || starting}>
              {t(screens.student_intro.button)}
            </Primary>
          </Card>
        )}

        {phase.kind === "questions" && current && (
          <Card>
            <p className="text-sm" style={{ color: PALETTE.muted }}>
              {t(UI.progress).replace("{n}", String(index + 1)).replace("{total}", String(visible.length))}
            </p>
            <h2 className="text-lg font-semibold leading-snug" style={{ color: PALETTE.tealInk }}>
              {t(current.text)}
            </h2>
            {/* «Можно выбрать несколько» уже стоит в тексте каждого вопроса с
                несколькими ответами (оба банка, az и ru) — отдельная подсказка
                повторяла его. Проверка — verify:pulse-routes, раздел 6. */}
            <div className="flex flex-col gap-2">
              {optionsOf(current).map((o) => {
                const a = answers[current.id];
                const selected = Array.isArray(a) ? a.includes(o.id) : a === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => choose(current, o)}
                    className="flex min-h-12 items-center gap-3 rounded-xl px-4 py-3 text-left text-base transition"
                    style={{
                      background: selected ? PALETTE.teal : PALETTE.paper,
                      color: selected ? "#fff" : PALETTE.ink,
                      border: `1px solid ${selected ? PALETTE.teal : PALETTE.line}`,
                    }}
                  >
                    {o.emoji && (
                      <span aria-hidden className="text-2xl">
                        {o.emoji}
                      </span>
                    )}
                    <span>{t(o.label)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 pt-2">
              {index > 0 ? <Secondary onClick={() => go(-1)}>{t(UI.back)}</Secondary> : <span />}
              <div className="flex gap-2">
                <Secondary onClick={skip}>{t(UI.skip)}</Secondary>
                <Primary onClick={() => go(1)}>{index === visible.length - 1 ? t(UI.send) : t(UI.next)}</Primary>
              </div>
            </div>
          </Card>
        )}

        {phase.kind === "sending" && (
          <Card>
            <p className="text-center" style={{ color: PALETTE.muted }}>
              {t(UI.sending)}
            </p>
          </Card>
        )}

        {phase.kind === "finish" && (
          <Card>
            <h1 className="text-xl font-semibold" style={{ color: PALETTE.tealInk }}>
              {t(screens.student_finish.title)}
            </h1>
            <p className="leading-relaxed">{t(screens.student_finish.body)}</p>
            <a
              href={`tel:${screens.student_finish.helpline.replace(/\D/g, "")}`}
              className="text-2xl font-bold"
              style={{ color: PALETTE.tealInk }}
            >
              {screens.student_finish.helpline}
            </a>
          </Card>
        )}

        {phase.kind === "state" && (
          <StateCard
            texts={STATES[phase.key]}
            langs={!localeKnown && LOCALE_UNKNOWN.states.includes(phase.key) ? UNKNOWN_LANGS : [locale]}
            onRetry={
              phase.key === "send_failed"
                ? () => void submit(answers)
                : phase.key === "entry_offline"
                  ? () => setEntryAttempt((n) => n + 1)
                  : phase.key === "turnstile_failed"
                    ? () => {
                        setCaptcha(null);
                        setCaptchaKey((k) => k + 1);
                        setPhase({ kind: "intro" });
                      }
                    : undefined
            }
          />
        )}
      </div>
    </main>
  );
}

// Состояние на одном языке или, пока класс не известен, на нескольких — блоками
// одинаковой величины, в порядке locale_unknown.show. Кнопка одна, подпись —
// через « / ».
function StateCard({ texts, langs, onRetry }: { texts: StateTexts; langs: PulseLocale[]; onRetry?: () => void }) {
  return (
    <Card>
      {langs.map((l) => (
        <div key={l} lang={l} className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold leading-snug" style={{ color: PALETTE.tealInk }}>
            {texts.title[l]}
          </h1>
          <p className="leading-relaxed">{texts.body[l]}</p>
        </div>
      ))}
      {texts.retry && onRetry && <Primary onClick={onRetry}>{langs.map((l) => texts.retry![l]).join(" / ")}</Primary>}
    </Card>
  );
}

function Card({ children }: { children: ReactNode }) {
  return (
    <section
      className="flex flex-col gap-4 rounded-2xl p-5"
      style={{ background: PALETTE.paper, border: `1px solid ${PALETTE.line}` }}
    >
      {children}
    </section>
  );
}

function Primary({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-12 rounded-xl px-5 py-3 text-base font-semibold transition disabled:opacity-50"
      style={{ background: PALETTE.amber, color: PALETTE.ink }}
    >
      {children}
    </button>
  );
}

function Secondary({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-12 rounded-xl px-4 py-3 text-base transition"
      style={{ background: "transparent", color: PALETTE.tealInk, border: `1px solid ${PALETTE.line}` }}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-16" role="status" aria-busy="true">
      <div
        className="h-8 w-8 animate-spin rounded-full border-4"
        style={{ borderColor: PALETTE.line, borderTopColor: PALETTE.teal }}
      />
    </div>
  );
}
